import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Briefcase, 
  MapPin, 
  Clock, 
  Users,
  CheckCircle,
  ArrowRight,
  Send,
  Loader2,
  Building2,
  Globe,
  Award,
  Heart,
  Zap,
  Target
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WEB3FORMS_KEY = 'f69f0cf2-eba4-4b0f-99c7-6df04e3d665f';

// Job Openings
const jobOpenings = [
  {
    id: 1,
    title: 'Field Verification Executive',
    type: 'Full-time / Freelance',
    location: 'Pan-India',
    description: 'Conduct on-ground verification of addresses, employment, and education across various locations.',
    requirements: [
      'Minimum 12th pass or graduate',
      'Good communication skills',
      'Own two-wheeler preferred',
      'Smartphone with internet',
      'Local area knowledge',
    ],
  },
  {
    id: 2,
    title: 'Verification Analyst',
    type: 'Full-time',
    location: 'Bhopal, MP',
    description: 'Analyze and process verification reports, coordinate with field executives, and ensure quality delivery.',
    requirements: [
      'Graduate in any discipline',
      '1-2 years experience in BGV/KYC',
      'Proficient in MS Office',
      'Strong analytical skills',
      'Good English communication',
    ],
  },
  {
    id: 3,
    title: 'Business Development Executive',
    type: 'Full-time',
    location: 'Bhopal / Remote',
    description: 'Generate leads, build client relationships, and drive business growth for verification services.',
    requirements: [
      'Graduate/MBA preferred',
      '2+ years in B2B sales',
      'Experience in HR Tech/BGV industry',
      'Strong presentation skills',
      'Proven sales track record',
    ],
  },
  {
    id: 4,
    title: 'Freelance Verification Partner',
    type: 'Freelance',
    location: 'All Major Cities',
    description: 'Partner with us on a project basis to conduct verifications in your city. Flexible timings and competitive payouts.',
    requirements: [
      'Minimum graduate',
      'Own conveyance',
      'Smartphone with camera',
      'Available for field work',
      'Background in investigations preferred',
    ],
  },
];

// Benefits
const benefits = [
  { icon: Zap, title: 'Flexible Work', description: 'Work on your own schedule with freelance options' },
  { icon: Award, title: 'Growth Opportunities', description: 'Career advancement in a growing industry' },
  { icon: Heart, title: 'Supportive Team', description: 'Collaborative and inclusive work culture' },
  { icon: Target, title: 'Competitive Pay', description: 'Attractive compensation and incentives' },
];

// Why Join Us
const whyJoinUs = [
  'Be part of India\'s trusted verification network',
  'Work with leading enterprises and institutions',
  'Continuous learning and skill development',
  'Performance-based growth opportunities',
  'Flexible work arrangements available',
  'Pan-India network and exposure',
];

export default function Careers() {
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    city: '',
    experience: '',
    jobType: 'Full-time',
    resume: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.email || !formData.phone || !formData.city) {
      toast.error('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const appliedFor = selectedJob ? selectedJob.title : 'General Application';
      
      // Store in database
      const applicationData = {
        ...formData,
        appliedFor: appliedFor,
        type: 'career_application',
      };
      await axios.post(`${API}/career-application`, applicationData);
      
      // Try to send email via Web3Forms (client-side) - may fail in some environments
      try {
        const web3formData = {
          access_key: WEB3FORMS_KEY,
          subject: `New Career Application: ${appliedFor} - ${formData.fullName}`,
          from_name: 'Aarambh Careers',
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          city: formData.city,
          experience: formData.experience || 'Not specified',
          job_type_preference: formData.jobType,
          resume_link: formData.resume || 'Not provided',
          message: formData.message || 'No message',
          applied_for: appliedFor,
        };

        await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(web3formData)
        });
      } catch (emailError) {
        console.log('Web3Forms email may have failed, but submission stored in database');
      }
      
      setIsSubmitted(true);
      toast.success('Your application has been submitted successfully!');
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        city: '',
        experience: '',
        jobType: 'Full-time',
        resume: '',
        message: '',
      });
      setSelectedJob(null);
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20 lg:py-28" data-testid="careers-hero">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(16, 185, 129, 0.1) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)'}}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-emerald-500/30">
              <Briefcase className="w-4 h-4" />
              Join Our Team
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-6">
              Build Your Career with
              <span className="block text-emerald-400">India's Trusted Verification Partner</span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              Join Aarambh India Service and be part of a growing team that's building trust 
              across India. We offer both permanent positions and freelance opportunities.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#openings">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white px-8" data-testid="view-openings-btn">
                  View Openings
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a href="#apply">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8" data-testid="apply-now-btn">
                  Apply Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{benefit.title}</h3>
                <p className="text-sm text-slate-500">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-20 lg:py-28 bg-slate-50" data-testid="why-join-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Why Join Aarambh?
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Be part of a mission-driven organization that's building trust and integrity 
                in India's hiring ecosystem.
              </p>
              <div className="space-y-4">
                {whyJoinUs.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-soft border border-slate-100">
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <Globe className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                  <p className="font-semibold text-slate-900">Pan-India</p>
                  <p className="text-sm text-slate-500">Network</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <Users className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                  <p className="font-semibold text-slate-900">500+</p>
                  <p className="text-sm text-slate-500">Team Members</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <Building2 className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                  <p className="font-semibold text-slate-900">100+</p>
                  <p className="text-sm text-slate-500">Cities Covered</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <Award className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                  <p className="font-semibold text-slate-900">ISO</p>
                  <p className="text-sm text-slate-500">9001:2015</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Openings */}
      <section id="openings" className="py-20 lg:py-28 bg-white" data-testid="job-openings">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Current Openings
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Explore opportunities to join our team - both permanent positions and freelance partnerships
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {jobOpenings.map((job) => (
              <Card key={job.id} className="border-slate-200 card-hover">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl text-slate-900 mb-2">{job.title}</CardTitle>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-1 rounded text-xs font-medium">
                          <Briefcase className="w-3 h-3" />
                          {job.type}
                        </span>
                        <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs font-medium">
                          <MapPin className="w-3 h-3" />
                          {job.location}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 mb-4">{job.description}</p>
                  <div className="mb-4">
                    <p className="font-medium text-slate-900 mb-2">Requirements:</p>
                    <ul className="space-y-1">
                      {job.requirements.map((req, i) => (
                        <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <a href="#apply">
                    <Button 
                      className="w-full bg-slate-900 hover:bg-slate-800"
                      onClick={() => setSelectedJob(job)}
                      data-testid={`apply-${job.id}-btn`}
                    >
                      Apply for this Role
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-20 lg:py-28 bg-slate-50" data-testid="application-form">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Apply Now
            </h2>
            <p className="text-lg text-slate-600">
              {selectedJob 
                ? `Applying for: ${selectedJob.title}` 
                : 'Submit your application and we\'ll find the right role for you'}
            </p>
          </div>
          
          <Card className="border-slate-200 shadow-soft">
            <CardContent className="p-8">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Application Submitted!</h3>
                  <p className="text-slate-600 mb-6">
                    Thank you for your interest. Our HR team will review your application 
                    and get back to you within 3-5 business days.
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => setIsSubmitted(false)}
                    data-testid="submit-another-application-btn"
                  >
                    Submit Another Application
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="fullName">Full Name *</Label>
                      <Input
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder="Your full name"
                        required
                        className="h-12"
                        data-testid="input-career-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        required
                        className="h-12"
                        data-testid="input-career-email"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 9876543210"
                        required
                        className="h-12"
                        data-testid="input-career-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="Your city"
                        required
                        className="h-12"
                        data-testid="input-career-city"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="experience">Years of Experience</Label>
                      <Input
                        id="experience"
                        name="experience"
                        value={formData.experience}
                        onChange={handleChange}
                        placeholder="e.g., 2 years"
                        className="h-12"
                        data-testid="input-career-experience"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="jobType">Preferred Job Type</Label>
                      <select
                        id="jobType"
                        name="jobType"
                        value={formData.jobType}
                        onChange={handleChange}
                        className="w-full h-12 px-3 rounded-md border border-slate-200 bg-white focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none"
                        data-testid="select-career-jobtype"
                      >
                        <option value="Full-time">Full-time</option>
                        <option value="Freelance">Freelance</option>
                        <option value="Both">Open to Both</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="resume">Resume/CV Link (Google Drive, LinkedIn, etc.)</Label>
                    <Input
                      id="resume"
                      name="resume"
                      value={formData.resume}
                      onChange={handleChange}
                      placeholder="Paste your resume link"
                      className="h-12"
                      data-testid="input-career-resume"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Why do you want to join Aarambh?</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about yourself and why you're interested..."
                      rows={4}
                      className="resize-none"
                      data-testid="input-career-message"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-12"
                    disabled={isSubmitting}
                    data-testid="submit-career-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Submit Application
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-slate-900" data-testid="careers-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
            Have Questions About Career Opportunities?
          </h2>
          <p className="text-slate-300 mb-6">
            Reach out to our HR team at <a href="mailto:info@aisverify.com" className="text-emerald-400 hover:underline">info@aisverify.com</a>
          </p>
          <p className="text-sm text-slate-500">
            CIN: U74920MP2020PTC051111 | ISO 9001:2015 Certified
          </p>
        </div>
      </section>
    </div>
  );
}
